import {
  CATEGORY_KEY_SEPARATOR,
  SELECTED_CATEGORIES_KEY,
} from '../constant/constant';
import {
  ConditionNode,
  PrimaryAgg,
  PrimaryCategoryNode,
  Product,
  SelectedFilters,
  StoredCategories,
  SubAgg,
  SubCategoryNode,
  TertiaryAgg,
  TertiaryCategoryNode,
} from '../interface/interface';

function splitCategoryPath(path: unknown): string[] {
  if (!path || typeof path !== 'string') return [];

  return path
    .split('>')
    .map((value) => value.trim())
    .filter(Boolean)
    .slice(0, 3);
}

const normalizeKey = (key: string) =>
  key
    .split(CATEGORY_KEY_SEPARATOR)
    .map((value) => value.trim().toLowerCase())
    .join(CATEGORY_KEY_SEPARATOR);

function getProductCategoryKeys(product: Product): string[] {
  const categoryPaths = Array.isArray(product.category_paths)
    ? product.category_paths
    : [];

  return categoryPaths
    .map(splitCategoryPath)
    .filter((segments) => segments.length > 0)
    .map((segments) => normalizeKey(segments.join(CATEGORY_KEY_SEPARATOR)));
}

const isSameOrDescendant = (key: string, ancestor: string) =>
  key === ancestor || key.startsWith(ancestor + CATEGORY_KEY_SEPARATOR);

export function buildCategoryTree(
  allProducts: Product[],
): PrimaryCategoryNode[] {
  const primaryMap = new Map<string, PrimaryAgg>();

  const products = Array.isArray(allProducts) ? allProducts : [];

  products.forEach((product) => {
    const categoryPaths = Array.isArray(product.category_paths)
      ? product.category_paths
      : [];

    categoryPaths.forEach((path) => {
      const [primary, secondary, tertiary] = splitCategoryPath(path);

      if (!primary) return;

      if (!primaryMap.has(primary)) {
        primaryMap.set(primary, {
          products: new Set<number>(),
          subMap: new Map<string, SubAgg>(),
        });
      }

      const primaryAgg = primaryMap.get(primary)!;
      primaryAgg.products.add(product.id);

      if (!secondary) return;

      if (!primaryAgg.subMap.has(secondary)) {
        primaryAgg.subMap.set(secondary, {
          products: new Set<number>(),
          tertiaryMap: new Map<string, TertiaryAgg>(),
        });
      }

      const subAgg = primaryAgg.subMap.get(secondary)!;
      subAgg.products.add(product.id);

      if (!tertiary) return;

      if (!subAgg.tertiaryMap.has(tertiary)) {
        subAgg.tertiaryMap.set(tertiary, { products: new Set<number>() });
      }

      subAgg.tertiaryMap.get(tertiary)!.products.add(product.id);
    });
  });

  const byLabel = (a: { label: string }, b: { label: string }) =>
    a.label.localeCompare(b.label);

  const tree: PrimaryCategoryNode[] = [];

  primaryMap.forEach((primaryAgg, primaryLabel) => {
    const subCategories: SubCategoryNode[] = [];

    primaryAgg.subMap.forEach((subAgg, subLabel) => {
      const tertiaryCategories: TertiaryCategoryNode[] = [];

      subAgg.tertiaryMap.forEach((tertiaryAgg, tertiaryLabel) => {
        tertiaryCategories.push({
          key: `${primaryLabel}|${subLabel}|${tertiaryLabel}`,
          label: tertiaryLabel,
          count: tertiaryAgg.products.size,
        });
      });

      subCategories.push({
        key: `${primaryLabel}|${subLabel}`,
        label: subLabel,
        count: subAgg.products.size,
        tertiaryCategories: tertiaryCategories.sort(byLabel),
      });
    });

    tree.push({
      label: primaryLabel,
      count: primaryAgg.products.size,
      subCategories: subCategories.sort(byLabel),
    });
  });

  return tree.sort(byLabel);
}

export function getProductCategoryLevels(product: Product): {
  primary: string[];
  secondary: string[];
  tertiary: string[];
} {
  const categoryPaths = Array.isArray(product.category_paths)
    ? product.category_paths
    : [];

  const primary = new Set<string>();
  const secondary = new Set<string>();
  const tertiary = new Set<string>();

  categoryPaths.forEach((path) => {
    const [primaryLabel, secondaryLabel, tertiaryLabel] =
      splitCategoryPath(path);

    if (primaryLabel) primary.add(primaryLabel);
    if (secondaryLabel) secondary.add(secondaryLabel);
    if (tertiaryLabel) tertiary.add(tertiaryLabel);
  });

  return {
    primary: Array.from(primary),
    secondary: Array.from(secondary),
    tertiary: Array.from(tertiary),
  };
}

export function filterProductsBySelection(
  products: Product[],
  filters: SelectedFilters,
): Product[] {
  const selectedKeys = [
    ...filters.primaryCategories,
    ...filters.subCategories,
    ...filters.tertiaryCategories,
  ].map(normalizeKey);

  const effectiveKeys = selectedKeys.filter(
    (key) =>
      !selectedKeys.some(
        (other) => other !== key && isSameOrDescendant(other, key),
      ),
  );

  const selectedConditions = filters.conditions.map((condition) =>
    condition.trim().toLowerCase(),
  );

  const selectedBrands = (
    Array.isArray(filters.brands) ? filters.brands : []
  ).map((brand) => brand.trim().toLowerCase());

  if (
    effectiveKeys.length === 0 &&
    selectedConditions.length === 0 &&
    selectedBrands.length === 0
  ) {
    return products;
  }

  return products.filter((product) => {
    const matchesCategory =
      effectiveKeys.length === 0 ||
      getProductCategoryKeys(product).some((productKey) =>
        effectiveKeys.some((key) => isSameOrDescendant(productKey, key)),
      );

    const matchesCondition =
      selectedConditions.length === 0 ||
      (Array.isArray(product.conditions) ? product.conditions : []).some(
        (condition) =>
          selectedConditions.includes(condition?.trim().toLowerCase()),
      );

    const matchesBrand =
      selectedBrands.length === 0 ||
      selectedBrands.includes(product.product_brand?.trim().toLowerCase());

    return matchesCategory && matchesCondition && matchesBrand;
  });
}

export function buildBrandList(allProducts: Product[]): ConditionNode[] {
  const brandCounts = new Map<string, number>();

  const products = Array.isArray(allProducts) ? allProducts : [];

  products.forEach((product) => {
    const brand = product.product_brand?.trim();
    if (!brand) return;

    brandCounts.set(brand, (brandCounts.get(brand) ?? 0) + 1);
  });

  return Array.from(brandCounts.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function buildConditionList(allProducts: Product[]): ConditionNode[] {
  const conditionCounts = new Map<string, number>();

  const products = Array.isArray(allProducts) ? allProducts : [];

  products.forEach((product) => {
    const conditions = Array.isArray(product.conditions)
      ? product.conditions
      : [];

    conditions.forEach((condition) => {
      const trimmed = condition?.trim();
      if (!trimmed) return;

      conditionCounts.set(trimmed, (conditionCounts.get(trimmed) ?? 0) + 1);
    });
  });

  return Array.from(conditionCounts.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count);
}

export function filterCategoryTree(
  tree: PrimaryCategoryNode[],
  query: string,
): PrimaryCategoryNode[] {
  const q = query.trim().toLowerCase();

  if (!q) return tree;

  const matches = (label: string) => label.toLowerCase().includes(q);

  return tree.reduce<PrimaryCategoryNode[]>((result, primary) => {
    if (matches(primary.label)) {
      result.push(primary);
      return result;
    }

    const matchingSubs = primary.subCategories.reduce<SubCategoryNode[]>(
      (subs, sub) => {
        if (matches(sub.label)) {
          subs.push(sub);
          return subs;
        }

        const matchingTertiary = sub.tertiaryCategories.filter((tertiary) =>
          matches(tertiary.label),
        );

        if (matchingTertiary.length > 0) {
          subs.push({ ...sub, tertiaryCategories: matchingTertiary });
        }

        return subs;
      },
      [],
    );

    if (matchingSubs.length > 0) {
      result.push({ ...primary, subCategories: matchingSubs });
    }

    return result;
  }, []);
}

export function saveSelectedCategories(filters: SelectedFilters) {
  const stored: StoredCategories = {
    primaryCategories: filters.primaryCategories,
    subCategories: filters.subCategories,
    tertiaryCategories: filters.tertiaryCategories,
    brands: filters.brands ?? [],
  };
  localStorage.setItem(SELECTED_CATEGORIES_KEY, JSON.stringify(stored));
}

export function readSelectedCategories(): StoredCategories | null {
  const stored = localStorage.getItem(SELECTED_CATEGORIES_KEY);
  if (!stored) return null;

  try {
    const parsed = JSON.parse(stored) as Partial<StoredCategories>;
    return {
      primaryCategories: parsed.primaryCategories ?? [],
      subCategories: parsed.subCategories ?? [],
      tertiaryCategories: parsed.tertiaryCategories ?? [],
      brands: parsed.brands ?? [],
    };
  } catch {
    return null;
  }
}
